/**
 * Support Center static data (V1).
 *
 * deliberately dumb data layer: UI components must only consume
 * `supportVideos` / `faqItems` (or a future async `supportApi.getVideos()`)
 * so the backend + Blob Storage swap later requires no UI changes.
 *
 * Video URLs below are temporary frontend placeholders. Do not add
 * Azure Blob / SAS / API logic here.
 */

export interface SupportVideo {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  duration?: string;
  category?: string;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export const SUPPORT_CONTACT_EMAIL = 'info@pixle.pro';

export const supportVideos: SupportVideo[] = [
  {
    id: 'create-first-video',
    title: 'Create your first video',
    description: 'Learn how to create your first video in Pixle.',
    thumbnailUrl: 'support/videos/create-first-video.webp',
    videoUrl: 'support/videos/create-first-video.mp4',
    duration: '2:24',
    category: 'getting-started',
  },
  {
    id: 'multi-characters',
    title: 'Use multiple characters in a video',
    description: 'Learn how to create and reuse characters in Pixle.',
    thumbnailUrl: 'support/videos/multi-characters.webp',
    videoUrl: 'support/videos/multi-characters.mp4',
    duration: '1:34',
    category: 'characters',
  },
  {
    id: 'collab-with-other-users',
    title: 'Collaborate with other users',
    description: 'Learn how to collaborate with other users in Pixle.',
    thumbnailUrl: 'support/videos/collab.webp',
    videoUrl: 'support/videos/collab.mp4',
    duration: '0:39',
    category: 'collaboration',
  },
  {
    id: 'homescreen',
    title: 'Navigating and using the home screen',
    description: 'Learn how to navigate and use the home screen in Pixle.',
    thumbnailUrl: 'support/videos/homescreen.webp',
    videoUrl: 'support/videos/homescreen.mp4',
    duration: '0:17',
    category: 'getting-started',
  },
];

/**
 * Future search hook (V1 does not render search — see spec §4).
 * Keep filtering here so adding a search input later needs no UI rework.
 */
export function filterSupportVideos(videos: SupportVideo[], query: string): SupportVideo[] {
  const q = query.trim().toLowerCase();
  if (!q) return videos;
  return videos.filter((v) =>
    `${v.title} ${v.description} ${v.category ?? ''}`.toLowerCase().includes(q),
  );
}

export function filterFaqItems(items: FAQItem[], query: string): FAQItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    `${item.question} ${item.answer}`.toLowerCase().includes(q),
  );
}

export const faqItems: FAQItem[] = [
  {
    id: 'what-is-pixle',
    question: 'What is Pixle?',
    answer:
      'Pixle is an AI-native animation studio for creators and storytellers. Create reusable characters, build animated scenes, and turn your ideas into high-quality animated videos while keeping creative control.',
  },

  {
    id: 'create-video',
    question: 'How do I create a video?',
    answer:
      'Start by selecting your characters and, optionally, an environment from your library. Describe the video you want to create, and Pixle turns your idea into a production-ready script. Review the script, then generate your video. You can save the result to your timeline and continue editing your project.',
  },

  {
    id: 'character-consistency',
    question: 'How does character consistency work?',
    answer:
      'Pixle lets you create reusable characters with their appearance, personality, voice, and reference images. When you use a character in a video, Pixle passes its reference information to the AI generation process to help maintain the character’s appearance and identity across scenes.',
  },

  {
    id: 'pixles',
    question: 'How do pixles work?',
    answer:
      'Pixles are Pixle’s usage credits for AI-powered generation. Different generation actions consume different amounts of pixles depending on the operation and its requirements. Your available balance is shown in the app, and you can purchase additional pixles when you need more generation capacity.',
  },

  {
    id: 'generation-failure',
    question: 'What happens if video generation fails?',
    answer:
      'If a video generation fails, Pixle does not charge you for an unsuccessful generation. You can review the error and retry the generation. If the issue persists, contact support and include the project or generation details so we can help investigate.',
  },
];
